import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, FileText, Plus, ArrowRight, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import AppLayout from '@/components/app/AppLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status styles for orders
const STATUS_STYLES = {
  draft: { bg: 'bg-gray-600/20', text: 'text-gray-400', label: 'Draft' },
  pending: { bg: 'bg-gray-600/20', text: 'text-gray-400', label: 'Pending' },
  pending_payment: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Payment Pending' },
  payment_pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Payment Pending' },
  payment_received: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Payment Received' },
  documents_pending: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'Documents Pending' },
  under_review: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', label: 'Under Review' },
  paid: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Paid' },
  in_progress: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'In Progress' },
  submitted: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', label: 'Submitted' },
  approved: { bg: 'bg-[#059669]/20', text: 'text-[#059669]', label: 'Approved' },
  completed: { bg: 'bg-emerald-600/20', text: 'text-emerald-400', label: 'Completed' },
  rejected: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Rejected' },
};

const DashboardPage = () => {
  const { user, token } = useAuth();
  
  const [companies, setCompanies] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companiesRes, ordersRes] = await Promise.all([
          axios.get(`${API}/companies`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setCompanies(companiesRes.data.companies || []);
        setOrders(ordersRes.data.orders || []);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    if (token) fetchData();
  }, [token]);

  const recentOrders = orders.slice(0, 5);
  const activeOrders = orders.filter(o => !['completed', 'rejected', 'draft'].includes(o.status)).length;
  const completedOrders = orders.filter(o => o.status === 'completed').length;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <AppLayout>
      {/* Welcome Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">
          Welcome back{user?.name ? `, ${user.name}` : ''}
        </h1>
        <p className="text-gray-400 text-sm mt-1">
          Here's an overview of your account
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-[#111827] border border-gray-800 rounded-lg p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Companies</p>
              <p className="text-2xl font-bold text-white mt-1">{companies.length}</p>
            </div>
            <div className="w-12 h-12 bg-[#059669]/10 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#059669]" />
            </div>
          </div>
        </div>
        
        <div className="bg-[#111827] border border-gray-800 rounded-lg p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Active Orders</p>
              <p className="text-2xl font-bold text-white mt-1">{activeOrders}</p>
            </div>
            <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-blue-400" />
            </div>
          </div>
        </div>
        
        <div className="bg-[#111827] border border-gray-800 rounded-lg p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Completed</p>
              <p className="text-2xl font-bold text-white mt-1">{completedOrders}</p>
            </div>
            <div className="w-12 h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-emerald-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <Link
          to="/dashboard/orders/new"
          className="bg-[#059669] hover:bg-[#00D46E] rounded-lg p-5 flex items-center justify-between transition-colors group"
          data-testid="new-order-btn"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-black/20 rounded-lg flex items-center justify-center">
              <Plus className="w-5 h-5 text-black" />
            </div>
            <div>
              <p className="font-semibold text-black">Place New Order</p>
              <p className="text-black/70 text-sm">Start a new service request</p>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-black group-hover:translate-x-1 transition-transform" />
        </Link>
        
        <Link
          to="/dashboard/companies"
          className="bg-[#111827] hover:bg-[#1C2128] border border-gray-800 rounded-lg p-5 flex items-center justify-between transition-colors group"
          data-testid="manage-companies-btn"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#059669]" />
            </div>
            <div>
              <p className="font-semibold text-white">Manage Companies</p>
              <p className="text-gray-400 text-sm">View and add your companies</p>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>

      {/* Recent Orders */}
      <div className="bg-[#111827] border border-gray-800 rounded-lg">
        <div className="p-5 border-b border-gray-800 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#059669]" />
            Recent Orders
          </h2>
          <Link 
            to="/dashboard/orders" 
            className="text-sm text-[#059669] hover:underline"
          >
            View all
          </Link>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-2 border-[#059669] border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-gray-400 mt-2">Loading...</p>
          </div>
        ) : recentOrders.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No orders yet</p>
            <Link 
              to="/dashboard/orders/new" 
              className="text-[#059669] text-sm hover:underline mt-2 inline-block"
            >
              Place your first order
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-gray-800">
            {recentOrders.map((order) => {
              const statusStyle = STATUS_STYLES[order.status] || STATUS_STYLES.pending;
              return (
                <Link
                  key={order.id}
                  to={`/dashboard/orders/${order.id}`}
                  className="p-4 flex items-center justify-between hover:bg-[#1C2128] transition-colors"
                  data-testid={`order-row-${order.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{order.product_name || 'Order'}</p>
                      <p className="text-gray-500 text-sm">{order.company_name || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                      {statusStyle.label}
                    </span>
                    <span className="text-gray-500 text-sm hidden md:block">
                      {formatDate(order.created_at)}
                    </span>
                    <ArrowRight className="w-4 h-4 text-gray-600" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default DashboardPage;
